import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Pagination, Select, SelectItem, Tag, TextArea } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';

type Item = {
  id: string; subject: string; kind: string; status: 'open' | 'acked' | 'resolved';
  expected: string | null; actual: string | null; diff: string; detail: Record<string, unknown>;
  firstSeenAt: string; lastSeenAt: string; ackNote: string | null; ackAt: string | null; ackByName: string | null; resolvedAt: string | null;
};
type Run = {
  id: string; trigger: 'nightly' | 'manual'; status: string; startedAt: string; finishedAt: string | null; error: string | null;
  summary: Record<string, { checked: number; mismatches: number; skipped: string | null } | number | string> | null;
};
type Recon = { items: Item[]; total: number; page: number; pageSize: number; counts: Record<string, number>; runs: Run[]; running: Run | null };

const KIND: Record<string, string> = {
  balance: 'Số dư người chơi', ledger: 'Sổ không có người chơi', round: 'Ván game', game: 'Game Miracle',
  jackpot: 'Hũ', pool: 'Quỹ game', miracle: 'Báo cáo Miracle',
};
const STATUS: Record<string, string> = { open: 'Đang mở', acked: 'Đã xác nhận', resolved: 'Đã khớp lại', all: 'Tất cả' };
const RUN_STATUS: Record<string, [string, 'green' | 'red' | 'blue' | 'gray' | 'magenta']> = {
  ok: ['Khớp', 'green'], mismatch: ['Có chênh lệch', 'red'], running: ['Đang chạy', 'blue'], error: ['Lỗi', 'magenta'], busy: ['Trùng lượt', 'gray'],
};
const WHY: Record<string, string> = {
  NO_LEDGER_ROW: 'không có dòng sổ', LEDGER_DIFFERS: 'dòng sổ khác số', MISSING_HERE: 'Miracle có, bên mình không có',
  STILL_HELD: 'bên mình còn treo', REFUSED_HERE: 'bên mình từ chối, Miracle tính', DIFFERS: 'khác loại hoặc số tiền',
  MISSING_AT_PROVIDER: 'bên mình có, Miracle không có', NO_JOURNAL: 'không có sổ', EDITED_OUTSIDE_GAME: 'bị sửa ngoài game',
  CHANGED_BETWEEN_JOURNAL_ROWS: 'bị sửa giữa hai lần game ghi', CHANGED_BETWEEN_LINES: 'bị sửa giữa hai lần game ghi',
  OTHER_GAME_OVERWRITTEN: 'bị ghi đè bằng số cũ', FILE_DIFFERS_FROM_JOURNAL: 'file quỹ khác sổ quỹ', UNREADABLE: 'không đọc được',
};

function Diff({ v, unit }: { v: string; unit?: string }) {
  const n = BigInt(v);
  return <span className={n === 0n ? 'num' : 'num num--neg'}>{n > 0n ? '+' : ''}{formatVnd(n)}{unit ? ' ' + unit : ''}</span>;
}
const amount = (v: string | null, unit?: string) => (v == null ? <span className="muted">không có</span> : <span className="num">{formatVnd(BigInt(v))}{unit ? ' ' + unit : ''}</span>);

// What the row is about, in words an operator can act on
function Subject({ i }: { i: Item }) {
  const d = i.detail;
  const s = (k: string) => (d[k] == null ? '' : String(d[k]));
  if (i.kind === 'balance') return <Link to={'/users/' + s('userId')}>#{s('userId')} {s('nick') || s('userName')} ({s('currency') === 'xu' ? 'xu' : 'tiền'})</Link>;
  if (i.kind === 'round') {
    const ma = (d.maVan as unknown[] | undefined) ?? [];
    return <span>{s('ledgerGame')} ván {String(ma[0] ?? '')}{Number(d.rounds) > 1 ? ` và ${Number(d.rounds) - 1} ván chạy cùng lúc` : ''}</span>;
  }
  if (i.kind === 'jackpot') return <span>Hũ {s('name')} ({s('part') === 'fund' ? 'quỹ hũ' : s('part') === 'journal' ? 'sổ hũ' : 'tiền hũ'})</span>;
  if (i.kind === 'pool') return <span>Quỹ game {s('gid')}</span>;
  if (i.kind === 'miracle' || i.kind === 'game') {
    return <span>Miracle {s('code')}{d.userId ? <> · <Link to={'/users/' + s('userId')}>người chơi #{s('userId')}</Link></> : null}</span>;
  }
  if (i.kind === 'ledger') return <span>{s('rows')} dòng sổ không có người chơi ({s('currency')})</span>;
  return <span>{i.subject}</span>;
}

function summaryLine(r: Run): string {
  if (r.error) return r.error;
  if (!r.summary) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(r.summary)) {
    if (!v || typeof v !== 'object' || !KIND[k]) continue;
    parts.push(v.skipped ? `${KIND[k]}: bỏ qua (${v.skipped})` : `${KIND[k]}: ${v.checked} mục, ${v.mismatches} lệch`);
  }
  return parts.join(' · ');
}

export function ReconPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [ack, setAck] = useState<Item | null>(null);
  const [note, setNote] = useState('');
  const q = useQuery({
    queryKey: ['recon', status, kind, page, pageSize],
    queryFn: () => api.get<Recon>('/api/admin/recon', { status, kind: kind || undefined, page, pageSize }),
    placeholderData: keepPreviousData,
    // a run in progress finishes within seconds to minutes
    refetchInterval: (query) => (query.state.data?.running ? 3000 : false),
  });
  const run = useMutation({
    mutationFn: () => api.post<{ run: Run }>('/api/admin/recon/run', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recon'] }),
  });
  const ackM = useMutation({
    mutationFn: (i: Item) => api.post(`/api/admin/recon/items/${i.id}/ack`, { note: note.trim() }),
    onSuccess: () => { setAck(null); setNote(''); qc.invalidateQueries({ queryKey: ['recon'] }); },
  });
  const d = q.data;
  const last = d?.runs.find((r) => r.status !== 'busy');
  const cols: ColumnDef<Item, any>[] = [
    { header: 'Loại', accessorKey: 'kind', cell: (c) => KIND[c.getValue()] ?? c.getValue() },
    { header: 'Đối tượng', id: 's', cell: (c) => <Subject i={c.row.original} /> },
    { header: 'Theo sổ', accessorKey: 'expected', cell: (c) => amount(c.getValue(), c.row.original.detail.unit as string | undefined) },
    { header: 'Thực tế', accessorKey: 'actual', cell: (c) => amount(c.getValue(), c.row.original.detail.unit as string | undefined) },
    { header: 'Lệch', accessorKey: 'diff', cell: (c) => <Diff v={c.getValue()} unit={c.row.original.detail.unit as string | undefined} /> },
    { header: 'Lý do', id: 'why', cell: (c) => WHY[String(c.row.original.detail.why ?? '')] ?? '-' },
    { header: 'Thấy lần đầu', accessorKey: 'firstSeenAt', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Tình trạng', id: 'st', cell: (c) => {
      const i = c.row.original;
      if (i.status === 'acked') return <span title={i.ackNote ?? ''}>Đã xác nhận: {i.ackNote}</span>;
      if (i.status === 'resolved') return <span>Đã khớp lại <When v={i.resolvedAt} /></span>;
      return can('recon.ack') ? <Button kind="tertiary" size="sm" onClick={() => { setAck(i); setNote(''); }}>Xác nhận</Button> : 'Đang mở';
    } },
  ];
  return (
    <div className="page">
      <PageHeader title="Đối soát tiền"
        lead="Mỗi đêm (và khi bấm chạy) hệ thống đối chiếu: số dư từng người chơi với sổ giao dịch, từng ván game với sổ, tiền hũ và quỹ game với sổ của chúng, và báo cáo ván của Miracle ngày hôm trước. Chênh lệch nào còn sau hai lần đọc cách nhau vài giây mới được ghi vào đây và báo Telegram cho chủ."
        actions={can('recon.run') ? (
          <Button renderIcon={Renew} disabled={!!d?.running || run.isPending} onClick={() => run.mutate()}>{d?.running ? 'Đang đối soát...' : 'Chạy đối soát ngay'}</Button>
        ) : null} />
      <InlineError error={run.error} />
      {d ? (
        <div className="kpis">
          <div className="kpi"><span className="kpi__label">Đang mở</span><strong className={'kpi__value num' + ((d.counts.open ?? 0) ? ' num--neg' : '')}>{d.counts.open ?? 0}</strong></div>
          <div className="kpi"><span className="kpi__label">Đã xác nhận</span><strong className="kpi__value num">{d.counts.acked ?? 0}</strong></div>
          <div className="kpi"><span className="kpi__label">Đã khớp lại</span><strong className="kpi__value num">{d.counts.resolved ?? 0}</strong></div>
          <div className="kpi"><span className="kpi__label">Lượt gần nhất</span><strong className="kpi__value">{last ? <><Tag type={RUN_STATUS[last.status]?.[1] ?? 'gray'}>{RUN_STATUS[last.status]?.[0] ?? last.status}</Tag> <When v={last.startedAt} /></> : 'Chưa chạy'}</strong></div>
        </div>
      ) : null}
      <section className="section">
        <h2>Chênh lệch</h2>
        <div className="toolbar-filters">
          <Select id="rc-status" labelText="Tình trạng" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            {Object.keys(STATUS).map((k) => <SelectItem key={k} value={k} text={STATUS[k]!} />)}
          </Select>
          <Select id="rc-kind" labelText="Loại" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}>
            <SelectItem value="" text="Tất cả" />
            {Object.keys(KIND).map((k) => <SelectItem key={k} value={k} text={KIND[k]!} />)}
          </Select>
        </div>
        {d && d.total === 0 && status === 'open' ? (
          <InlineNotification kind="success" lowContrast hideCloseButton title="Khớp" subtitle="Không có chênh lệch nào đang mở." />
        ) : (
          <DataTable columns={cols} data={d?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error} onRetry={() => q.refetch()}
            rowKey={(r) => r.id} empty={{ title: 'Không có mục nào' }} />
        )}
        {d && d.total > 0 ? (
          <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={d.total}
            onChange={({ page: p, pageSize: s }) => { setPage(p); setPageSize(s); }} />
        ) : null}
      </section>
      <section className="section">
        <h2>Các lượt đối soát</h2>
        <DataTable<Run> columns={[
          { header: 'Bắt đầu', accessorKey: 'startedAt', cell: (c) => <When v={c.getValue()} /> },
          { header: 'Kiểu', accessorKey: 'trigger', cell: (c) => (c.getValue() === 'nightly' ? 'Hằng đêm' : 'Chạy tay') },
          { header: 'Kết quả', accessorKey: 'status', cell: (c) => <Tag type={RUN_STATUS[c.getValue()]?.[1] ?? 'gray'}>{RUN_STATUS[c.getValue()]?.[0] ?? c.getValue()}</Tag> },
          { header: 'Chi tiết', id: 'sum', cell: (c) => <span className="muted">{summaryLine(c.row.original)}</span> },
        ]} data={d?.runs} loading={q.isLoading} error={q.error} rowKey={(r) => r.id} empty={{ title: 'Chưa có lượt nào' }} />
      </section>
      <Modal open={!!ack} modalHeading="Xác nhận chênh lệch" primaryButtonText="Xác nhận" secondaryButtonText="Hủy"
        primaryButtonDisabled={note.trim().length < 5 || ackM.isPending} onRequestClose={() => setAck(null)}
        onRequestSubmit={() => ack && note.trim().length >= 5 && ackM.mutate(ack)}>
        {ack ? (
          <div className="stack">
            <p><Subject i={ack} />: lệch <Diff v={ack.diff} unit={ack.detail.unit as string | undefined} />.</p>
            <p>Xác nhận nghĩa là đã biết vì sao lệch (ví dụ lịch sử trước khi có sổ, một sự cố đã ghi lại). Mục này sẽ im lặng khi số lệch giữ nguyên, và mở lại, báo lại cho chủ nếu số lệch đổi.</p>
            <TextArea id="ack-note" labelText="Lý do (bắt buộc)" value={note} maxCount={500} enableCounter onChange={(e) => setNote(e.target.value)} />
            <InlineError error={ackM.error} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
